import { useMemo } from 'react';
import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/Button';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../../lib/hooks';
import { fetcher } from '../../../lib/api-fetcher';
import { Plus } from 'lucide-react';

interface TemplateListPageItem {
  id: string;
  name: string;
  status: string;
  version: number;
  subscriberCount: number;
  durationCycles: number;
  createdAt: string;
}

const templatesConfig = defineListPage<TemplateListPageItem>({
  variant: 'data-table',
  resource: 'templates',
  columns: [
    { id: 'name', header: 'Template Name', accessor: 'name', sortable: true },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      renderType: 'status',
      renderMeta: {
        colorMap: {
          ACTIVE: 'success',
          DRAFT: 'warning',
          RETIRED: 'info',
        },
      },
    },
    { id: 'version', header: 'Version', accessor: 'version', numeric: true },
    { id: 'subscriberCount', header: 'Members', accessor: 'subscriberCount', numeric: true },
    { id: 'durationCycles', header: 'Cycles', accessor: 'durationCycles', numeric: true },
  ],
  defaultPageSize: 20,
  onRowClick: (row) => window.open(`/templates/${row.id}`, '_self'),
  rowActions: [
    {
      label: 'View Template',
      onClick: (row) => window.open(`/templates/${row.id}`, '_self'),
    },
    {
      label: 'Edit Template',
      onClick: (row) => window.open(`/templates/${row.id}`, '_self'),
    },
  ],
});

const fetchTemplates: PaginatedQueryFn<TemplateListPageItem> = async (params) => {
  const qs = new URLSearchParams({ page: params.page || '1', limit: params.limit || '20' }).toString();
  return fetcher.get<{ data: TemplateListPageItem[]; meta: { total: number; page: number; limit: number } }>(
    `/staff/v1/chit-groups/templates?${qs}`,
  );
};

export const Route = createFileRoute('/_auth/templates/')({
  component: TemplateList,
});

function TemplateList() {
  usePageTitle('Scheme Templates');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const urlAdapter = useMemo(
    () => createUrlAdapter(search, navigate as never),
    [search, navigate],
  );

  const list = useListPage(templatesConfig, fetchTemplates, urlAdapter);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Scheme Templates"
        subtitle="Reusable scheme definitions. Chit groups snapshot their terms from a template at creation."
        action={
          <Button asChild variant="primary">
            <Link to="/templates/new">
              <Plus className="h-4 w-4 mr-1.5" />
              New Template
            </Link>
          </Button>
        }
      />

      <DataTable
        config={templatesConfig}
        list={list}
      />
    </div>
  );
}
